<template>
  <div
    v-if="fullScreen && show"
    class="full-screen-modal d-flex flex-column"
    :class="modalClass"
  >
    <div
      class="full-screen-modal__header p-3 d-flex justify-content-between"
      v-if="!hideHeader"
    >
      <div class="d-flex justify-content-between">
        <slot name="header-control" />
      </div>
      <div class="full-screen-modal__header__title text-uppercase text-center">
        <slot name="header-title">{{ title | trans }}</slot>
      </div>
      <div class="full-screen-modal__header__close text-white">
        <slot name="header-close">
          <b-link v-ripple @click="onClose" />
        </slot>
      </div>
    </div>

    <div class="flex-grow-1 full-screen-modal__body" ref="body">
      <slot />
    </div>

    <div
      class="full-screen-modal__footer p-3 w-100 text-white text-center"
      v-if="!hideFooter"
    >
      <slot name="footer">
        <app-button
          variant="transparent"
          @click="onClose"
          block
          class="text-uppercase text-white m-0"
          >{{ "ui.close" | trans }}</app-button
        >
      </slot>
    </div>
  </div>
  <b-modal
    v-else-if="show"
    v-model="show"
    @close="onClose"
    :modal-class="modalClass"
    :content-class="contentClass"
    :header-class="headerClass"
    :footer-class="footerClass"
    :dialog-class="dialogClass"
    :body-class="bodyClass"
    :hide-header="hideHeader"
    :hide-footer="hideFooter"
    :no-close-on-backdrop="!closable"
    :no-close-on-esc="!closable"
  >
    <template #modal-header>
      <div class="modal__top-control">
        <slot name="header-control" />
      </div>
      <div class="flex-grow-1 modal__top-title display-5">
        <slot name="header-title">
          {{ title | trans }}
        </slot>
      </div>
      <div class="modal__top-close">
        <slot name="header-close">
          <i class="modal__close-icon" @click="onClose" />
        </slot>
      </div>
    </template>

    <slot />

    <template #modal-footer>
      <slot name="footer">
        <div class="text-center w-100">
          <app-button variant="transparent" block @click.prevent="onClose">{{
            "ui.close" | trans
          }}</app-button>
        </div>
      </slot>
    </template>
  </b-modal>
</template>

<script>
import AppButton from "@/components/ui/Button";

export default {
  name: "AppModal",
  components: { AppButton },
  props: {
    value: {
      type: Boolean,
      required: false,
      default: false,
    },
    title: {
      title: String,
      required: false,
      default: "",
    },
    fullScreen: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideHeader: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideFooter: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalClass: {
      type: [String, Array, Object],
      required: false,
      default: null,
    },
    dialogClass: {
      type: [String, Array, Object],
      required: false,
      default: null,
    },
    contentClass: {
      type: [String, Array, Object],
      required: false,
      default: null,
    },
    headerClass: {
      type: [String, Array, Object],
      required: false,
      default: null,
    },
    footerClass: {
      type: [String, Array, Object],
      required: false,
      default: null,
    },
    bodyClass: {
      type: [String, Array, Object],
      required: false,
      default: null,
    },
    closable: {
      type: Boolean,
      required: false,
      default: true,
    },
    name: {
      type: String,
      required: false,
      default: null,
    },
  },
  mounted() {
    if (this.fullScreen) {
      document.body.classList.add("modal-open");
    }
  },
  beforeDestroy() {
    if (this.fullScreen) {
      document.body.classList.remove("modal-open");
    }
  },
  methods: {
    onClose() {
      this.show = false;
    },
    gtmTrack() {
      if (!this.name) {
        return;
      }

      if (this.$gtm) {
        this.$gtm.trackEvent({
          category: "Modal",
          action: "open",
          label: this.name,
        });
      }
    },
  },
  watch: {
    value: {
      handler(value) {
        if (this.value === true) {
          this.gtmTrack();
        }
        this.show = value;
      },
      immediate: true,
    },
    show(value) {
      this.$emit("input", value);
      if (!value) {
        this.$emit("close");
      }
    },
  },
  data() {
    return {
      show: this.value,
    };
  },
};
</script>

<style lang="scss" scoped>
.modal {
  @include themify($themes) {
    &-footer {
      .btn {
        margin-bottom: 0;
        &-light {
          background-color: transparent;
          border-color: transparent;
          color: lighten(theme-get("small", "primary"), 5%);
        }
      }
    }
    &__top-title {
      padding-left: 2rem;
      padding-right: 2rem;
      color: themed("primary-darken-3");
    }
    &__close-icon {
      cursor: pointer;
      padding: 0.5rem;
      width: 2rem;
      position: absolute;
      right: 1.5rem;
      top: 50%;
      @include translateY(-50%);
    }
    .modal-content.current-theme-primary-gradient {
      .modal {
        &__top-title {
          color: $white;
        }
      }
    }
  }
}

.full-screen-modal {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: $zindex-modal;
  color: $white;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  content: "";

  &__header {
    &__title {
      letter-spacing: 0.125rem;
      font-size: 1.125rem;
      @extend .ringside-con-smi-bld;
    }
    &__close {
      .btn {
        margin-bottom: 0;
      }
      opacity: 1;
      a {
        width: 1.3125rem;
        height: 1.3125rem;
        position: relative;
        display: block;
        &:before,
        &:after {
          content: "";
          position: absolute;
          width: 1.875rem;
          height: 0.0625rem;
          display: block;
          top: 50%;
          background-color: $white;
          @include origin(50% 50%);
        }
        &:before {
          @include rotate(-45deg);
        }
        &:after {
          @include rotate(45deg);
        }
      }
    }
  }
}
</style>
